// UserControlCenter.jsx — v140 ⚙ مركز التحكم الموحّد للمستخدم.
// كل شيء عن المستخدم في مكان واحد: الملف الشخصي · الدور · صفحاته · الصلاحيات
// الدقيقة (منح/منع لكل إجراء) · كلمة المرور · الحذف — بزر حفظ واحد.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatErr } from "./lib/api";
import { showToast, confirmWithUndo } from "./lib/theme";
import { ALL_TABS } from "./tabs";
import PermissionInspector from "./PermissionInspector";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";

const SECTIONS = [
  { id: "profile", icon: "👤", label: "الملف الشخصي" },
  { id: "access", icon: "🛡️", label: "الدور والصفحات" },
  { id: "fine", icon: "🎯", label: "صلاحيات دقيقة" },
  { id: "security", icon: "🔐", label: "الأمان" },
];

export default function UserControlCenter({ user, currentUser, availableRoles = [], onClose, onSaved, onDeleted }) {
  const [sec, setSec] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [inspecting, setInspecting] = useState(false); // v149 — فاحص الصلاحيات
  // ── الملف
  const [profile, setProfile] = useState({
    name: user.name || "", full_name: user.full_name || "", phone: user.phone || "",
    email: user.email || "", bio: user.bio || "", employee_number: user.employee_number || "",
  });
  // ── الدور + الصفحات
  const [role, setRole] = useState(user.role);
  const roleTabs = useMemo(() => ALL_TABS.filter((t) => t.roles.includes(role)), [role]);
  const [pages, setPages] = useState(() => new Set(
    Array.isArray(user.page_permissions) ? user.page_permissions
      : ALL_TABS.filter((t) => t.roles.includes(user.role)).map((t) => t.id)));
  // ── صلاحيات دقيقة
  const [catalog, setCatalog] = useState(null);
  const [rolePerms, setRolePerms] = useState(new Set());
  const [extra, setExtra] = useState(new Set(user.extra_permissions || []));
  const [denied, setDenied] = useState(new Set(user.denied_permissions || []));
  // ── الأمان
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");

  const isSelf = user.id === currentUser?.id;
  const isAdminUser = role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const [c, r] = await Promise.all([api.get("/rbac/catalog"), api.get("/rbac/roles")]);
        setCatalog(c.data?.groups || []);
        window.__uccRoles = r.data?.roles || [];
        const found = (r.data?.roles || []).find((x) => x.role === role);
        setRolePerms(new Set(found?.permissions || []));
      } catch (e) { showToast({ kind: "danger", message: formatErr(e) }); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const found = (window.__uccRoles || []).find((x) => x.role === role);
    if (found) setRolePerms(new Set(found.permissions || []));
  }, [role]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  // ── حالة كل صلاحية دقيقة
  const stateOf = (key) => (denied.has(key) ? "deny" : extra.has(key) ? "grant" : rolePerms.has(key) ? "role_on" : "role_off");
  const setPermState = (key, mode) => {
    const ex = new Set(extra), dn = new Set(denied);
    ex.delete(key); dn.delete(key);
    if (mode === "grant") ex.add(key);
    if (mode === "deny") dn.add(key);
    setExtra(ex); setDenied(dn);
  };
  const viewOnly = (group) => {
    const ex = new Set(extra), dn = new Set(denied);
    group.items.forEach((it) => {
      ex.delete(it.key); dn.delete(it.key);
      if (it.key.endsWith(".view")) { if (!rolePerms.has(it.key)) ex.add(it.key); }
      else if (rolePerms.has(it.key)) dn.add(it.key);
    });
    setExtra(ex); setDenied(dn);
  };
  const fullControl = (group) => {
    const ex = new Set(extra), dn = new Set(denied);
    group.items.forEach((it) => {
      dn.delete(it.key);
      if (!rolePerms.has(it.key)) ex.add(it.key); else ex.delete(it.key);
    });
    setExtra(ex); setDenied(dn);
  };
  const overridesCount = extra.size + denied.size;

  const togglePage = (id) => setPages((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const changeRole = (newRole) => {
    setRole(newRole);
    setPages(new Set(ALL_TABS.filter((t) => t.roles.includes(newRole)).map((t) => t.id)));
  };

  const save = async () => {
    if (pw || pw2) {
      if (pw.length < 8) { showToast({ kind: "danger", message: "كلمة المرور 8 أحرف كحد أدنى" }); setSec("security"); return; }
      if (pw !== pw2) { showToast({ kind: "danger", message: "كلمتا المرور غير متطابقتين" }); setSec("security"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...profile,
        page_permissions: isAdminUser ? ALL_TABS.map((t) => t.id) : [...pages],
        extra_permissions: [...extra],
        denied_permissions: [...denied],
      };
      if (!isSelf && role !== user.role) payload.role = role;
      if (isSelf && role !== user.role) { /* لا يغيّر المدير دوره بنفسه */ }
      if (pw) payload.password = pw;
      await api.patch(`/auth/users/${user.id}`, payload);
      showToast({ kind: "success", message: `✅ حُفظت كل إعدادات ${user.username}` });
      onSaved?.();
      onClose();
    } catch (e) { showToast({ kind: "danger", message: formatErr(e) }); }
    setSaving(false);
  };

  const deleteUser = () => {
    confirmWithUndo({
      message: `🗑 سيتم حذف المستخدم ${user.username} نهائياً`,
      duration: 5000,
      onConfirm: async () => {
        try {
          await api.delete(`/auth/users/${user.id}`);
          showToast({ kind: "success", message: "✅ حُذف المستخدم" });
          onDeleted?.();
          onClose();
        } catch (e) { showToast({ kind: "danger", message: formatErr(e) }); }
      },
      onUndo: () => {},
    });
  };

  const F = ({ label, k, type = "text", placeholder = "", ltr = false, textarea = false }) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 700, display: "block", marginBottom: 4, color: "var(--brown-deep, #3f2f23)" }}>{label}</label>
      {textarea ? (
        <textarea rows={3} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
          data-testid={`ucc-${k}`} placeholder={placeholder} className="sim-input" style={{ width: "100%", fontSize: 12.5 }} />
      ) : (
        <input type={type} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
          data-testid={`ucc-${k}`} placeholder={placeholder} className="sim-input"
          style={{ width: "100%", fontSize: 13, direction: ltr ? "ltr" : undefined, textAlign: ltr ? "left" : undefined }} />
      )}
    </div>
  );

  const S = {
    role_on: ["حسب الدور ✓", "#F0FDF4", "#166534"], role_off: ["غير ممنوحة", "#f5f5f4", "#78716c"],
    grant: ["ممنوحة ✚", "#EFF6FF", "#1D4ED8"], deny: ["ممنوعة ⛔", "#FEE2E2", "#B91C1C"],
  };

  return createPortal(
    <div dir="rtl" data-testid="user-control-center"
      style={{ position: "fixed", inset: 0, zIndex: zVar('extra_2200'), background: "rgba(30,20,10,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 880, height: "min(92vh, 760px)", background: "#fffdf8", borderRadius: 20,
                 boxShadow: "0 24px 70px rgba(0,0,0,.35)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #efe8d8", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "linear-gradient(135deg,#6B4E3D,#b08756)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, flexShrink: 0 }}>
              {(profile.full_name || profile.name || user.username).slice(0, 1)}
            </div>
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 15, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {profile.full_name || profile.name || user.username}
                {isSelf && <span style={{ fontSize: 11, color: "var(--gold, #b08756)", marginInlineStart: 6 }}>(أنت)</span>}
              </b>
              <span style={{ fontSize: 11.5, color: "#a8a29e" }}>@{user.username} · إدارة شاملة من مكان واحد</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => setInspecting(true)} data-testid="ucc-inspect-btn"
 
              style={{ height: 34, borderRadius: 12, border: "1px solid #e8dfc9", background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 800, padding: "0 12px", color: "#6B4E3D", whiteSpace: "nowrap" }} aria-label="ماذا يرى هذا الموظف بالضبط؟">
              🔍 فاحص الصلاحيات
            </button>
            <button onClick={onClose} data-testid="ucc-close"
              style={{ width: 34, height: 34, borderRadius: 12, border: "1px solid #e8dfc9", background: "#fff", cursor: "pointer", fontSize: 15 }}>✕</button>
          </div>
        </div>
        {inspecting && <PermissionInspector userId={user.id} onClose={() => setInspecting(false)} />}

        {/* Section nav */}
        <div style={{ display: "flex", gap: 6, padding: "10px 16px", borderBottom: "1px solid #efe8d8", overflowX: "auto" }}>
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSec(s.id)} data-testid={`ucc-sec-${s.id}`}
              style={{
                padding: "8px 15px", borderRadius: 12, fontSize: 12.5, fontWeight: 800, whiteSpace: "nowrap",
                border: "1px solid " + (sec === s.id ? "transparent" : "#e8dfc9"), cursor: "pointer", fontFamily: "inherit",
                background: sec === s.id ? "var(--brown-deep, #6B4E3D)" : "#fff", color: sec === s.id ? "#fff" : "#57534e",
                transition: "background-color .15s, color .15s",
              }}>
              {s.icon} {s.label}
              {s.id === "fine" && overridesCount > 0 && (
                <span style={{ marginInlineStart: 5, fontSize: 10, background: sec === s.id ? "rgba(255,255,255,.25)" : "#f0e9dc", padding: "1px 7px", borderRadius: 10 }}>{overridesCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {sec === "profile" && (
            <div style={{ maxWidth: 560 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <F label="الاسم المعروض" k="name" />
                <F label="الاسم الثلاثي" k="full_name" placeholder="عبدالله محمد العتيبي" />
                <F label="رقم الجوال" k="phone" type="tel" placeholder="05XXXXXXXX" ltr />
                <F label="البريد الإلكتروني" k="email" type="email" placeholder="example@email.com" ltr />
              </div>
              <F label="🔗 رقم الموظف في Malachite HCM (يفعّل البونص والحضور)" k="employee_number" placeholder="مثلاً: 1013" ltr />
              <F label="📝 نبذة (يقرأها الذكاء الاصطناعي لتخصيص تجربته)" k="bio" textarea placeholder="مدير الفرع، يهتم بالجودة، يدير شفت المساء…" />
            </div>
          )}

          {sec === "access" && (
            <div>
              <div style={{ marginBottom: 16, maxWidth: 380 }}>
                <label style={{ fontSize: 11, fontWeight: 700, display: "block", marginBottom: 4 }}>الدور الأساسي</label>
                {isSelf || user.role === "admin" ? (
                  <div style={{ padding: "8px 12px", background: "#f0e9dc", borderRadius: 10, fontSize: 13, fontWeight: 800 }}>
                    {(availableRoles.find((r) => r.role === role)?.label) || role}
                    <span style={{ fontSize: 10.5, fontWeight: 400, color: "#78716c", marginInlineStart: 8 }}>
                      {isSelf ? "لا يمكنك تغيير دورك بنفسك" : "دور المدير محمي"}
                    </span>
                  </div>
                ) : (
                  <select value={role} onChange={(e) => changeRole(e.target.value)} data-testid="ucc-role" className="sim-input"
                    style={{ width: "100%", fontSize: 13, fontWeight: 700 }}>
                    {availableRoles.map((r) => (
                      <option key={r.role} value={r.role}>{r.label || r.role}{!r.is_system ? " (مخصّص)" : ""}</option>
                    ))}
                  </select>
                )}
                <div style={{ fontSize: 10.5, color: "#a8a29e", marginTop: 4 }}>تغيير الدور يعيد تحديد الصفحات لافتراضي الدور الجديد — عدّلها كما تشاء بالأسفل.</div>
              </div>

              {/* v190.8 — زر توحيد واحد يمسح الحقول الثلاثة دفعة واحدة */}
              {!isAdminUser && !isSelf && (
                <div data-testid="ucc-unify-role-default-wrap"
                     style={{ padding: 12, background: "#F0FDF4",
                               border: "1px solid #A7F3D0", borderRadius: 12,
                               fontSize: 12.5, color: "#166534",
                               display: "flex", justifyContent: "space-between",
                               alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 220, lineHeight: 1.6 }}>
                    🔄 <b>توحيد على افتراضي الدور</b>
                    <div style={{ fontSize: 11, color: "#78716C", marginTop: 3 }}>
                      إجراء واحد يمسح: صفحات مخصّصة + منح فردية + منع فردية.
                      يعود المستخدم إلى صلاحيات دوره ({role}) بالضبط.
                    </div>
                  </div>
                  <button data-testid="ucc-unify-role-default"
                           className="btn btn-soft"
                           style={{ background: "#DCFCE7", color: "#166534",
                                     border: "1px solid #86EFAC",
                                     padding: "8px 14px", fontSize: 12.5,
                                     fontWeight: 700, cursor: "pointer",
                                     borderRadius: 10 }}
                           onClick={async () => {
                             if (!window.confirm(
                               `توحيد ${user.username} على افتراضي الدور (${role})؟\n` +
                               `سيُمسح: page_permissions + extra_permissions + denied_permissions.`
                             )) return;
                             try {
                               setSaving(true);
                               const r = await api.post(
                                 `/auth/users/${encodeURIComponent(user.username)}/reset-to-role-default`
                               );
                               showToast(r.data?.message || "تم التوحيد", "success");
                               // Reset local state to reflect the fresh server view.
                               setPages(new Set(roleTabs.map((t) => t.id)));
                               setExtra(new Set());
                               setDenied(new Set());
                               if (onSaved) onSaved();
                             } catch (e) {
                               showToast(formatErr(e) || "فشل التوحيد", "error");
                             } finally { setSaving(false); }
                           }}
                           disabled={saving}>
                    🔄 وحّد الآن على افتراضي الدور
                  </button>
                  <button data-testid="ucc-logout-all-devices" className="btn btn-soft" disabled={saving}
                    style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
                             padding: "8px 14px", fontSize: 12.5, fontWeight: 700, borderRadius: 10, marginInlineStart: 8 }}
                    onClick={async () => {
                      if (!window.confirm(`تسجيل خروج ${user.username} من كل الأجهزة؟`)) return;
                      try {
                        setSaving(true);
                        await api.post(`/auth/users/${encodeURIComponent(user.id)}/logout-all`);
                        showToast("سُجّل الخروج من كل الأجهزة", "success");
                      } catch (e) { showToast(formatErr(e) || "تعذّر", "error"); } finally { setSaving(false); }
                    }}>
                    🚪 تسجيل الخروج من كل الأجهزة
                  </button>
                </div>
              )}

              {isAdminUser ? (
                <div style={{ padding: 12, background: "rgba(212,175,55,0.12)", borderRadius: 12, fontSize: 12.5 }}>
                  👑 المدير العام يرى كل الصفحات تلقائياً.
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    <b style={{ fontSize: 13 }}>📄 الصفحات التي يراها — <span style={{ color: "var(--gold,#b08756)" }}>{pages.size}</span> من {ALL_TABS.length}</b>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-soft" style={{ fontSize: 10, padding: "3px 9px" }} data-testid="ucc-pages-role"
                        onClick={() => setPages(new Set(roleTabs.map((t) => t.id)))}>افتراضي الدور</button>
                      <button className="btn btn-soft" style={{ fontSize: 10, padding: "3px 9px" }} data-testid="ucc-pages-all"
                        onClick={() => setPages(new Set(ALL_TABS.map((t) => t.id)))}>الكل</button>
                      <button className="btn btn-soft" style={{ fontSize: 10, padding: "3px 9px" }} data-testid="ucc-pages-none"
                        onClick={() => setPages(new Set())}>لا شيء</button>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 6 }}>
                    {ALL_TABS.map((t) => {
                      const on = pages.has(t.id);
                      const inRole = t.roles.includes(role);
                      return (
                        <label key={t.id} data-testid={`ucc-page-${t.id}`}
                          style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, padding: "7px 9px", borderRadius: 10, cursor: "pointer",
                                   background: on ? "#f6f1e6" : "#fff", border: "1px solid " + (on ? "#d9c9a8" : "#eee7d8") }}>
                          <input type="checkbox" checked={on} onChange={() => togglePage(t.id)} />
                          <span style={{ flex: 1 }}>{t.label}</span>
                          {!inRole && <span style={{ fontSize: 9, color: "#D97706" }}>خارج الدور</span>}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {sec === "fine" && (
            <div>
              {isAdminUser ? (
                <div style={{ padding: 12, background: "rgba(212,175,55,0.12)", borderRadius: 12, fontSize: 12.5 }}>👑 المدير العام يملك كل الصلاحيات — لا حاجة للتخصيص.</div>
              ) : (<>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                  <div style={{ fontSize: 11.5, color: "#78716c", lineHeight: 1.7 }}>
                    لكل إجراء: <b style={{ color: "#166534" }}>حسب الدور</b> أو <b style={{ color: "#1D4ED8" }}>منح ✚</b> أو <b style={{ color: "#B91C1C" }}>منع ⛔</b> — التخصيص يخص هذا المستخدم فقط.
                  </div>
                  {overridesCount > 0 && (
                    <button className="btn btn-soft" style={{ fontSize: 10.5, padding: "4px 10px" }} data-testid="ucc-fine-reset"
                      onClick={() => { setExtra(new Set()); setDenied(new Set()); }}>↺ إرجاع الكل لافتراضي الدور</button>
                  )}
                </div>
                {!catalog && <div style={{ textAlign: "center", padding: 26, color: "#a8a29e" }}>⏳</div>}
                {(catalog || []).map((g) => (
                  <div key={g.name} style={{ border: "1px solid #eee7d8", borderRadius: 14, padding: 12, marginBottom: 10, background: "#fff" }} data-testid={`ucc-perm-group-${g.name}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 6, flexWrap: "wrap" }}>
                      <b style={{ fontSize: 13 }}>📄 {g.name}</b>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-soft" style={{ fontSize: 10, padding: "3px 10px" }}
                          onClick={() => viewOnly(g)} data-testid={`ucc-viewonly-${g.name}`}
  aria-label="يمنح صلاحيات العرض فقط ويمنع التعديل في هذا القسم">👁 عرض فقط</button>
                        <button className="btn btn-soft" style={{ fontSize: 10, padding: "3px 10px", color: "#1D4ED8" }}
                          onClick={() => fullControl(g)} data-testid={`ucc-fullcontrol-${g.name}`}
  aria-label="يمنح كل صلاحيات هذا القسم (عرض + تعديل + إجراءات)">🎛 عرض مع تحكم</button>
                      </div>
                    </div>
                    {g.items.map((it) => {
                      const st = stateOf(it.key);
                      return (
                        <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f3ede0" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>{it.label}</div>
                            <div style={{ fontSize: 10, color: "#a8a29e" }}>{it.description}</div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 9px", borderRadius: 12, background: S[st][1], color: S[st][2], whiteSpace: "nowrap" }}>{S[st][0]}</span>
                          <select className="sim-input" style={{ width: 112, fontSize: 11, padding: "4px 6px" }}
                            value={st === "grant" ? "grant" : st === "deny" ? "deny" : "role"}
                            onChange={(e) => setPermState(it.key, e.target.value)}
                            data-testid={`ucc-perm-${it.key}`}>
                            <option value="role">حسب الدور</option>
                            <option value="grant">منح ✚</option>
                            <option value="deny">منع ⛔</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>)}
            </div>
          )}

          {sec === "security" && (
            <div style={{ maxWidth: 480 }}>
              <b style={{ fontSize: 13.5, display: "block", marginBottom: 4 }}>🔑 تعيين كلمة مرور جديدة</b>
              <p style={{ fontSize: 11.5, color: "#78716c", margin: "0 0 10px", lineHeight: 1.7 }}>
                8 أحرف كحد أدنى · حرف كبير + صغير + رقم + رمز. اتركها فارغة إذا لا تريد التغيير — تُحفظ مع زر الحفظ بالأسفل.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input type="password" value={pw} autoComplete="new-password" placeholder="كلمة المرور الجديدة"
                  onChange={(e) => setPw(e.target.value)} data-testid="ucc-pw" className="sim-input" style={{ fontSize: 13 }} />
                <input type="password" value={pw2} autoComplete="new-password" placeholder="تأكيد كلمة المرور"
                  onChange={(e) => setPw2(e.target.value)} data-testid="ucc-pw2" className="sim-input" style={{ fontSize: 13 }} />
              </div>
              {pw && pw2 && pw !== pw2 && <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 6 }}>⚠ كلمتا المرور غير متطابقتين</div>}

              {!isSelf && (
                <div style={{ marginTop: 28, border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 14, padding: 14 }}>
                  <b style={{ fontSize: 13, color: "#B91C1C" }}>⚠️ منطقة الخطر</b>
                  <p style={{ fontSize: 11.5, color: "#7f1d1d", margin: "4px 0 10px" }}>حذف المستخدم نهائي — يفقد الوصول فوراً ولا يمكن التراجع بعد التأكيد.</p>
                  <button className="btn" onClick={deleteUser} data-testid="ucc-delete-user"
                    style={{ background: "#DC2626", color: "#fff", fontSize: 12 }}>🗑 حذف المستخدم {user.username}</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid #efe8d8", display: "flex", gap: 8, alignItems: "center", background: "#fffdf8" }}>
          <button className="btn btn-primary" style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }} disabled={saving}
            onClick={save} data-testid="ucc-save">
            {saving ? "جارٍ الحفظ…" : "💾 حفظ كل التغييرات"}
          </button>
          <button className="btn btn-outline" onClick={onClose} style={{ fontSize: 13 }}>إلغاء</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
